import { useFormContext } from "react-hook-form";
import type { BidPostData } from "validations";

import { ControlledWrapper } from "@krea/shared/design-system/input/controlled-wrapper";
import { FieldLabel } from "@krea/shared/design-system/input/field";
import { IntegerInput } from "@krea/shared/design-system/input/integer-input";
import { MoneyInput } from "@krea/shared/design-system/input/money-input";
import { PercentageInput } from "@krea/shared/design-system/input/percentage-input";
import type { Currency } from "@krea/shared/types/common";

import type { IPurchaseFinancingBidInput } from "@/features/lender-responses/types/lender-response";

import DetailsItem from "../../../../details/item";
import {
  AdministrationFeeTooltip,
  administrationFeeLabelText,
} from "../../bidHelpers";

interface IFormProps {
  bidFormIndex: number;
  currency: Currency;
  isEditing: boolean;
  isInterestRatePerYearActivated?: boolean;
}

export const PurchaseFinancingForm = ({
  bidFormIndex,
  currency,
  isEditing,
  isInterestRatePerYearActivated,
}: IFormProps) => {
  const { control } = useFormContext<BidPostData<IPurchaseFinancingBidInput>>();

  return (
    <div className="flex w-full flex-col">
      <div className="w-full space-y-4">
        <DetailsItem
          title={
            isEditing
              ? "Update purchase order financing terms"
              : "Purchase financing terms"
          }
          className="mb-4"
        >
          <div className="w-full">
            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.amount`}
              label="Amount"
              required
              render={({ field }) => (
                <MoneyInput {...field} currency={currency} id={field.name} />
              )}
            />
          </div>
        </DetailsItem>

        <DetailsItem title="" className="mb-4">
          <div className="w-full space-y-4">
            <span className="mb-2 ml-3 block text-sm italic text-gray-600">
              Here you can explain other costs & qualitative information about
              your offer
            </span>

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.lenderMonthlyInterest`}
              label={
                isInterestRatePerYearActivated
                  ? "Interest (per year)"
                  : "Interest (per month)"
              }
              required
              render={({ field }) => (
                <PercentageInput
                  {...field}
                  id={field.name}
                  placeholder='Value in "%"'
                />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.paybackTimeDays`}
              label="Payback time (days)"
              required
              render={({ field }) => (
                <IntegerInput
                  {...field}
                  id={field.name}
                  placeholder="Number of days"
                />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.limitFeePerYear`}
              label="Limit fee (per year)"
              render={({ field }) => (
                <PercentageInput
                  {...field}
                  id={field.name}
                  placeholder='Value in "%"'
                />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.setupFee`}
              label="Setup fee"
              render={({ field }) => (
                <MoneyInput {...field} currency={currency} id={field.name} />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.administrationFees`}
              render={({ field }) => (
                <>
                  <div className="flex items-center gap-2">
                    <FieldLabel>{administrationFeeLabelText}</FieldLabel>
                    <AdministrationFeeTooltip />
                  </div>
                  <MoneyInput {...field} currency={currency} id={field.name} />
                </>
              )}
            />
          </div>
        </DetailsItem>
      </div>
    </div>
  );
};
